import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Bot, RotateCcw, Save } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  Card,
  ConfirmDialog,
  ErrorState,
  LoadingState,
  Textarea,
  useToast,
} from '@/components/ui'
import { useAuthStore } from '@/stores/auth.ts'

interface PromptData {
  text: string
  version: number
  updatedAt: string
}

function extractErrorMessage(err: unknown): string {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
    (err as Error)?.message ??
    String(err)
  )
}

export function ClearanceAiPromptSection() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const isAdmin = user?.role === 'admin'

  const [promptText, setPromptText] = useState('')
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false)
  const [confirmResetOpen, setConfirmResetOpen] = useState(false)
  const [isDirty, setIsDirty] = useState(false)

  const { data, isLoading, error } = useQuery<PromptData>({
    queryKey: ['clearance-ai-prompt'],
    queryFn: async () => {
      const res = await apiClient.get('/dispo/clearance/ai-prompt')
      return res.data as PromptData
    },
    enabled: isAdmin,
  })

  useEffect(() => {
    if (data) {
      setPromptText(data.text)
      setIsDirty(false)
    }
  }, [data])

  const saveMutation = useMutation({
    mutationFn: async (text: string) => {
      const res = await apiClient.post('/dispo/clearance/ai-prompt', { text })
      return res.data as { version: number; changed: boolean }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['clearance-ai-prompt'] })
      setIsDirty(false)
      if (result.changed) {
        toast({ title: `Prompt gespeichert (Version ${result.version})`, variant: 'success' })
      } else {
        toast({ title: 'Prompt unverändert – keine neue Version erstellt', variant: 'default' })
      }
    },
    onError: (err) => {
      toast({ title: 'Fehler beim Speichern', description: extractErrorMessage(err), variant: 'error' })
    },
  })

  const resetMutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post('/dispo/clearance/ai-prompt/reset')
      return res.data as { version: number; changed: boolean }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['clearance-ai-prompt'] })
      setIsDirty(false)
      if (result.changed) {
        toast({ title: `Standardprompt wiederhergestellt (Version ${result.version})`, variant: 'success' })
      } else {
        toast({ title: 'Prompt war bereits Standard – keine Änderung', variant: 'default' })
      }
    },
    onError: (err) => {
      toast({ title: 'Fehler beim Zurücksetzen', description: extractErrorMessage(err), variant: 'error' })
    },
  })

  if (!isAdmin) return null

  if (isLoading) return <LoadingState />
  if (error) return <ErrorState message={extractErrorMessage(error)} />

  const updatedAt = data?.updatedAt ? new Date(data.updatedAt).toLocaleString('de-DE') : '–'

  return (
    <>
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <Bot className="w-4 h-4 text-brand-600" />
            <h2 className="text-h3">KI-Prompt (Admin)</h2>
          </div>
          <p className="text-caption text-gray-500 mt-1">
            Systemweiter Prompt für die KI-gestützte Plausibilitätsprüfung.
            Jede Änderung erzeugt eine neue Prompt-Version und macht alle gespeicherten KI-Prüfergebnisse ungültig.
          </p>
        </Card.Header>
        <Card.Body className="space-y-4">
          <div className="flex items-center gap-3 text-caption text-gray-500">
            <span>
              Version: <strong>{data?.version ?? '–'}</strong>
            </span>
            <span>·</span>
            <span>Zuletzt geändert: {updatedAt}</span>
          </div>

          <Textarea
            rows={14}
            value={promptText}
            onChange={(e) => {
              setPromptText(e.target.value)
              setIsDirty(e.target.value.trim() !== (data?.text ?? '').trim())
            }}
            label="Prompt-Text"
            hint="Der Prompt wird vor jeder KI-Prüfung mit den Ticketdaten und Leistungen ergänzt."
          />

          {isDirty && (
            <div className="flex items-start gap-2 text-caption text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Ungespeicherte Änderungen. Das Speichern erzeugt eine neue Prompt-Version.</span>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Button
              variant="primary"
              size="sm"
              onClick={() => setConfirmSaveOpen(true)}
              loading={saveMutation.isPending}
              disabled={!isDirty || !promptText.trim()}
            >
              <Save className="w-4 h-4 mr-1" />
              Speichern
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmResetOpen(true)}
              loading={resetMutation.isPending}
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Standardprompt wiederherstellen
            </Button>
          </div>
        </Card.Body>
      </Card>

      <ConfirmDialog
        open={confirmSaveOpen}
        onClose={() => setConfirmSaveOpen(false)}
        title="Prompt speichern?"
        description="Diese Änderung erhöht die Prompt-Version. Alle bisher gespeicherten KI-Prüfergebnisse werden als veraltet markiert und beim nächsten Lauf erneut geprüft."
        confirmLabel="Speichern"
        variant="default"
        onConfirm={() => {
          setConfirmSaveOpen(false)
          saveMutation.mutate(promptText)
        }}
      />

      <ConfirmDialog
        open={confirmResetOpen}
        onClose={() => setConfirmResetOpen(false)}
        title="Standardprompt wiederherstellen?"
        description="Der aktuelle Prompt wird durch den eingebauten Standardprompt ersetzt. Falls der Text abweicht, wird eine neue Prompt-Version erzeugt und der KI-Cache invalidiert."
        confirmLabel="Wiederherstellen"
        variant="default"
        onConfirm={() => {
          setConfirmResetOpen(false)
          resetMutation.mutate()
        }}
      />
    </>
  )
}
